import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  Calendar,
  User,
  Truck,
  FileText,
  X,
  Download,
  Tag,
  Clipboard,
  Clock
} from 'lucide-react';
import { maintenanceService } from '../services/maintenanceService';
import exportHtmlSheetFromTemplate from '../utils/exportHtmlSheetFromTemplate';
import IntegerInput from './common/IntegerInput';

const MaintenanceModal = ({ isOpen, onClose, truck, exitData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [generatedPDF, setGeneratedPDF] = useState(false);

  const [formData, setFormData] = useState({
    serie: '',
    kilometrajeActual: 0,
    tipoMantenimiento: 'Preventivo',
    fechaMantenimiento: new Date().toISOString().split('T')[0],
    nombreOperador: '',
    nombreJefeTaller: '',
    nombreMecanico: '',
    // Valor por defecto solicitado
    nombreSupervisor: 'Ing. José Antonio Leal De La Garza',
    requisicionMaterial1: exitData?.requisition_number || '',
    requisicionMaterial2: '',
    requisicionMaterial3: '',
    requisicionMaterial4: '',
    requisicionMaterial5: '',
    requisicionMaterial6: '',
    requisicionMaterial7: '',
    // Valor por defecto solicitado
    observaciones: 'Ver informacion anexada.'
  });

  useEffect(() => {
    if (truck) {
      console.log('Datos del vehículo para mantenimiento:', truck);
      console.log('Datos de la salida:', exitData);
      setFormData(prev => ({
        ...prev,
        // Aceptar varias formas del campo Serie por seguridad
        serie: truck.serie || truck.Serie || truck.SERIE || '',
        kilometrajeActual: truck.km_actual || 0,
        numeroEconomico: truck.numero_economico || '', // Guardamos el número económico
        // Mantener el número de requisición de la salida si existe
        requisicionMaterial1: exitData?.requisition_number || prev.requisicionMaterial1 || ''
      }));
      
      // Verificar si la serie está presente
      if (!truck.serie) {
        console.error('Error: El vehículo no tiene número de serie definido');
        setError('El vehículo seleccionado no tiene un número de serie válido. Por favor, actualice los datos del vehículo primero.');
      }
    }
  }, [truck]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setGeneratedPDF(false);
    
    try {
      setIsSubmitting(true);
      
      // Validaciones detalladas
      // Si no está en formData, intentar tomarla del objeto truck (varias formas)
      const serieValue = (formData.serie || truck?.serie || truck?.Serie || truck?.SERIE || '').toString();
      if (!serieValue || serieValue.trim() === '') {
        throw new Error('El número de serie es obligatorio y no puede estar vacío');
      }
      
      console.log('Datos del formulario a enviar:', formData);

      if (!formData.kilometrajeActual || formData.kilometrajeActual <= 0) {
        throw new Error('El kilometraje actual debe ser mayor que cero');
      }
      
      // Validar que tengamos el ID del vehículo
      if (!truck || !truck.id) {
        throw new Error('No se pudo identificar correctamente el vehículo. Por favor, inténtelo de nuevo');
      }

      // Crear el mantenimiento
      const maintenanceData = {
        // Asegurarnos de enviar serie (fallback a truck)
        serie: formData.serie || truck?.serie || truck?.Serie || truck?.SERIE || '',
        kilometrajeActual: formData.kilometrajeActual,
        tipoMantenimiento: formData.tipoMantenimiento,
        fechaMantenimiento: formData.fechaMantenimiento,
        nombreOperador: formData.nombreOperador,
        nombreJefeTaller: formData.nombreJefeTaller, 
        nombreMecanico: formData.nombreMecanico,
        nombreSupervisor: formData.nombreSupervisor,
        requisicionMaterial1: formData.requisicionMaterial1,
        requisicionMaterial2: formData.requisicionMaterial2,
        requisicionMaterial3: formData.requisicionMaterial3,
        requisicionMaterial4: formData.requisicionMaterial4,
        requisicionMaterial5: formData.requisicionMaterial5,
        requisicionMaterial6: formData.requisicionMaterial6,
        requisicionMaterial7: formData.requisicionMaterial7,
        observaciones: formData.observaciones,
        truckId: truck.id,
        numeroEconomico: truck.numero_economico || formData.numeroEconomico || '' // Agregamos el número económico
      };

      const result = await maintenanceService.create(maintenanceData);
      console.log('✅ Mantenimiento creado:', result);
      
      setSuccess(true);

      // Generar PDF automáticamente
      if (result && result.id) {
        try {
          await maintenanceService.generatePDF(result.id);
          setGeneratedPDF(true);
        } catch (pdfError) {
          console.error('Error generando PDF:', pdfError);
          // No mostrar este error al usuario para no confundirlo,
          // ya que el registro fue creado correctamente
        }
        // Eliminado: ya no se genera el archivo .htm
      }
      
      // Esperar un poco antes de cerrar el modal
      setTimeout(() => {
        onClose(true); // true indica que se realizó un cambio
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error creando mantenimiento:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-orange-600" />
              Generar Mantenimiento
            </h2>
            <button
              onClick={() => onClose()}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <motion.div
              className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 rounded"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-red-700 font-medium">{error}</p>
            </motion.div>
          )}

          {success && (
            <motion.div
              className="bg-green-100 border-l-4 border-green-500 p-4 mb-6 rounded"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-green-700 font-medium">
                ✅ Mantenimiento registrado correctamente
              </p>
              {generatedPDF && (
                <p className="text-green-700">
                  📄 Formato PDF generado y descargado
                </p>
              )}
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Información de la unidad */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                <Truck className="w-5 h-5 text-gray-600" />
                Información de la Unidad
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Número Económico
                  </label>
                  <input
                    type="text"
                    value={truck?.numero_economico || ''}
                    disabled
                    className="w-full bg-gray-100 border border-gray-300 rounded px-3 py-2 text-gray-700"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Serie/VIN
                  </label>
                  <input
                    type="text"
                    value={formData.serie}
                    onChange={(e) => handleChange('serie', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Kilometraje Actual
                  </label>
                  <IntegerInput
                    name="kilometrajeActual"
                    value={formData.kilometrajeActual}
                    onChange={(e) => handleChange('kilometrajeActual', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Ingrese kilometraje actual"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Fecha de Mantenimiento
                  </label>
                  <input
                    type="date"
                    value={formData.fechaMantenimiento}
                    onChange={(e) => handleChange('fechaMantenimiento', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Tipo de mantenimiento */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-gray-600" />
                Tipo de Mantenimiento
              </label>
              <select
                value={formData.tipoMantenimiento}
                onChange={(e) => handleChange('tipoMantenimiento', e.target.value)}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              >
                {maintenanceService.getTipoMantenimientoOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Personal */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-gray-600" />
                Personal Involucrado
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Nombre Operador
                  </label>
                  <input
                    type="text"
                    value={formData.nombreOperador}
                    onChange={(e) => handleChange('nombreOperador', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Jefe de Taller
                  </label>
                  <input
                    type="text"
                    value={formData.nombreJefeTaller}
                    onChange={(e) => handleChange('nombreJefeTaller', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Mecánico
                  </label>
                  <input
                    type="text"
                    value={formData.nombreMecanico}
                    onChange={(e) => handleChange('nombreMecanico', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Supervisor
                  </label>
                  <input
                    type="text"
                    value={formData.nombreSupervisor}
                    onChange={(e) => handleChange('nombreSupervisor', e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Requisiciones de material */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                <Clipboard className="w-5 h-5 text-gray-600" />
                Requisiciones de Material
              </h3>
              
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map(num => (
                  <div key={num}>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Material {num}
                    </label>
                    <input
                      type="text"
                      value={formData[`requisicionMaterial${num}`]}
                      onChange={(e) => handleChange(`requisicionMaterial${num}`, e.target.value)}
                      className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder={`Descripción del material ${num}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Observaciones */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-600" />
                Observaciones
              </label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => handleChange('observaciones', e.target.value)}
                rows={3}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Observaciones adicionales..."
              />
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                type="button"
                onClick={() => onClose()}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Guardar y Generar PDF
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MaintenanceModal;